import React, { useEffect, useMemo, useState } from 'react'
import gql from 'graphql-tag'
import { useQuery } from '@apollo/client'
import Skeleton from 'react-loading-skeleton'
import moment from 'moment-timezone'
import { Input } from 'reactstrap'
import './HistoryList.css'
import DatetimeRenderer from "./DatetimeRenderer"

const BQHistoryQuery = gql`
    query BQHistoryQuery($objectIds: [ID!]!, $startDate: Date, $endDate: Date) {
        bqHistory(objectIds: $objectIds, startDate: $startDate, endDate: $endDate) {
            items {
                appLabel
                modelName
                objectId
                eventTimestamp
                actorName
                isNew
                isDeleted
                snapshot
                dirtyFields
            }
        }
    }
`

const formatJson = (value) => {
    if (value === null || value === undefined) return '--'
    if (typeof value === 'string') return value

    try {
        return JSON.stringify(value, null, 2)
    } catch {
        return String(value)
    }
}

const HIDDEN_CHANGE_FIELDS = new Set(['date_modified'])
const SNAPSHOT_FIELD_ALIASES = {
    modified_by: 'modified_by_id',
    created_by: 'created_by_id',
    assigned_by: 'assigned_by_id',
}

const formatFieldLabel = (fieldName) => {
    if (!fieldName) return '--'
    return fieldName
        .split('_')
        .filter(Boolean)
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(' ')
}

const formatValue = (value) => {
    if (value === null || value === undefined || value === '') return '{ NULL }'
    if (typeof value === 'string') return value

    try {
        return JSON.stringify(value, null, 2)
    } catch {
        return String(value)
    }
}

const normalizeForComparison = (value) => {
    if (value === null || value === undefined || value === '') return null
    if (Array.isArray(value)) return value.map(normalizeForComparison)
    if (value && typeof value === 'object') {
        return Object.keys(value)
            .sort()
            .reduce((acc, key) => {
                acc[key] = normalizeForComparison(value[key])
                return acc
            }, {})
    }
    return value
}

const areValuesEqual = (left, right) => (
    JSON.stringify(normalizeForComparison(left)) === JSON.stringify(normalizeForComparison(right))
)

const getSnapshotFieldName = (fieldName) => SNAPSHOT_FIELD_ALIASES[fieldName] || fieldName
const TODAY = moment().format(moment.HTML5_FMT.DATE)
const DEFAULT_END_DATE = TODAY
const DEFAULT_START_DATE = moment().subtract(6, 'days').format(moment.HTML5_FMT.DATE)
const MAX_HISTORY_RANGE_DAYS = 90

function BQHistoryList({ objectIds = [], emptyText = 'No history found yet.' }) {
    const [visibleDetails, setVisibleDetails] = useState({})
    const [startDate, setStartDate] = useState(DEFAULT_START_DATE)
    const [endDate, setEndDate] = useState(DEFAULT_END_DATE)
    const [appliedStartDate, setAppliedStartDate] = useState(DEFAULT_START_DATE)
    const [appliedEndDate, setAppliedEndDate] = useState(DEFAULT_END_DATE)
    const normalizedObjectIds = useMemo(
        () => (objectIds || []).filter(Boolean),
        [objectIds]
    )
    // const shouldShowSource = normalizedObjectIds.length > 1
    const isDefaultDateRange = appliedStartDate === DEFAULT_START_DATE && appliedEndDate === DEFAULT_END_DATE
    const hasPendingFilterChanges = startDate !== appliedStartDate || endDate !== appliedEndDate
    const hasMissingDate = !startDate || !endDate
    const hasInvalidDateRange = !!(startDate && endDate && startDate > endDate)
    const hasRangeTooLarge = !!(
        startDate
        && endDate
        && moment(endDate).diff(moment(startDate), 'days') > (MAX_HISTORY_RANGE_DAYS - 1)
    )

    useEffect(() => {
        setVisibleDetails({})
    }, [normalizedObjectIds.join('|'), appliedStartDate, appliedEndDate])

    const { data, loading, error } = useQuery(BQHistoryQuery, {
        variables: {
            objectIds: normalizedObjectIds,
            startDate: appliedStartDate || null,
            endDate: appliedEndDate || null,
        },
        skip: !normalizedObjectIds.length,
        fetchPolicy: 'cache-and-network',
    })

    const filterControls = (
        <div className="vgm-history-filters">
            <div className="vgm-history-filter-field">
                <label className="vgm-history-filter-label" htmlFor="bq-history-start-date">From</label>
                <Input
                    id="bq-history-start-date"
                    type="date"
                    value={startDate}
                    min={endDate ? moment(endDate).subtract(MAX_HISTORY_RANGE_DAYS - 1, 'days').format(moment.HTML5_FMT.DATE) : undefined}
                    max={endDate || undefined}
                    disabled={loading}
                    onChange={(e) => setStartDate(e.target.value)}
                />
            </div>
            <div className="vgm-history-filter-field">
                <label className="vgm-history-filter-label" htmlFor="bq-history-end-date">To</label>
                <Input
                    id="bq-history-end-date"
                    type="date"
                    value={endDate}
                    min={startDate || undefined}
                    max={startDate ? moment.min(moment(startDate).add(MAX_HISTORY_RANGE_DAYS - 1, 'days'), moment(TODAY)).format(moment.HTML5_FMT.DATE) : TODAY}
                    disabled={loading}
                    onChange={(e) => setEndDate(e.target.value)}
                />
            </div>
            {hasPendingFilterChanges && (
                <button
                    type="button"
                    className="vgm-history-apply-filters"
                    onClick={() => {
                        setAppliedStartDate(startDate)
                        setAppliedEndDate(endDate)
                    }}
                    disabled={hasMissingDate || hasInvalidDateRange || hasRangeTooLarge || loading}
                >
                    Apply Filters
                </button>
            )}
        </div>
    )

    const historyItems = data?.bqHistory?.items || []
    const getItemKey = (item, index) => (
        `${item.appLabel}-${item.modelName}-${item.objectId}-${item.eventTimestamp || index}-${index}`
    )
    const toggleDetails = (key) => {
        setVisibleDetails((prev) => ({ ...prev, [key]: !prev[key] }))
    }

    if (loading && !historyItems.length) {
        return (
            <div className="vgm-history-list">
                {filterControls}
                <Skeleton height={180} count={3} className="mb-2" />
            </div>
        )
    }

    if (error) {
        return <div className="alert alert-danger mb-0">{error.message}</div>
    }

    if (!historyItems.length) {
        return (
            <div className="vgm-history-list">
                {filterControls}
                {hasMissingDate && (
                    <p className="vgm-history-empty mb-0">Please select both a start date and an end date.</p>
                )}
                {hasInvalidDateRange && (
                    <p className="vgm-history-empty mb-0">The start date must be on or before the end date.</p>
                )}
                {hasRangeTooLarge && (
                    <p className="vgm-history-empty mb-0">Please select a date range of 60 days or less.</p>
                )}
                <p className="vgm-history-empty mb-0">
                    {isDefaultDateRange
                        ? 'No history found in the last 7 days. Try expanding the date range.'
                        : emptyText}
                </p>
            </div>
        )
    }

    return (
        <div className="vgm-history-list">
            {filterControls}
            {hasMissingDate && (
                <p className="vgm-history-empty mb-0">Please select both a start date and an end date.</p>
            )}
            {hasInvalidDateRange && (
                <p className="vgm-history-empty mb-0">The start date must be on or before the end date.</p>
            )}
            {hasRangeTooLarge && (
                <p className="vgm-history-empty mb-0">Please select a date range of 60 days or less.</p>
            )}
            {historyItems.map((item, index) => {
                const itemKey = getItemKey(item, index)
                const visibleChanges = item.dirtyFields && typeof item.dirtyFields === 'object'
                    ? Object.entries(item.dirtyFields).filter(([fieldName, oldValue]) => {
                        const snapshotFieldName = getSnapshotFieldName(fieldName)
                        return (
                            !HIDDEN_CHANGE_FIELDS.has(fieldName)
                            && !areValuesEqual(oldValue, item.snapshot?.[snapshotFieldName])
                        )
                    })
                    : []
                // const isDetailsVisible = visibleDetails[itemKey] ?? shouldShowDetailsByDefault(item)
                const isDetailsVisible = visibleDetails[itemKey]
                const cardClassName = [
                    'vgm-history-card',
                    item.isNew ? 'vgm-history-card-created' : '',
                    item.isDeleted ? 'vgm-history-card-deleted' : '',
                ].filter(Boolean).join(' ')
                return (
                    <div key={itemKey} className={cardClassName}>
                        <div className="vgm-history-card-header">
                            <div>
                                <div className="vgm-history-card-title">
                                    {item.modelName || '--'} #{item.objectId}
                                </div>
                                <div className="vgm-history-card-meta">
                                    {item.eventTimestamp ? <DatetimeRenderer datetime={item.eventTimestamp} /> : '--'}
                                    {' | '}
                                    {item.actorName || 'System'}
                                </div>
                            </div>
                            <div className="vgm-history-badges">
                                {item.isNew && <span className="vgm-history-badge vgm-history-badge-new">Created</span>}
                                {item.isDeleted && <span className="vgm-history-badge vgm-history-badge-deleted">Deleted</span>}
                            </div>
                        </div>

                        {!item.isNew && !item.isDeleted && <div className="vgm-history-section">
                            <div className="vgm-history-section-label">Changes</div>
                            {visibleChanges.length > 0 ? (
                                <div className="vgm-history-change-list">
                                    {visibleChanges.map(([fieldName, oldValue]) => {
                                        const snapshotFieldName = getSnapshotFieldName(fieldName)
                                        return (
                                            <div key={fieldName} className="vgm-history-change-row">
                                                <div className="vgm-history-change-field">{formatFieldLabel(fieldName)}</div>
                                                <div className="vgm-history-change-inline">
                                                    <span className="vgm-history-change-chip vgm-history-change-chip-old">
                                                        {/* <span className="vgm-history-change-chip-label">Before</span> */}
                                                        <span className="vgm-history-change-chip-value">{formatValue(oldValue)}</span>
                                                    </span>
                                                    <span className="vgm-history-change-arrow">
                                                        <i className="fa fa-arrow-right" />
                                                    </span>
                                                    <span className="vgm-history-change-chip vgm-history-change-chip-new">
                                                        {/* <span className="vgm-history-change-chip-label">After</span> */}
                                                        <span className="vgm-history-change-chip-value">
                                                            {formatValue(item.snapshot?.[snapshotFieldName])}
                                                        </span>
                                                    </span>
                                                </div>
                                            </div>
                                        )
                                    })}
                                </div>
                            ) : (
                                <div className="vgm-history-empty">No changes found for this record.</div>
                            )}
                        </div>}

                        <button type="button" className="vgm-history-toggle" onClick={() => toggleDetails(itemKey)}>
                            {isDetailsVisible
                                ? 'Hide Record Details'
                                : 'View Record Details'}
                        </button>

                        {isDetailsVisible && (
                            <div className="vgm-history-section">
                                <div className="vgm-history-section-label">Record Details</div>
                                <pre className="vgm-history-json mb-0">{formatJson(item.snapshot)}</pre>
                            </div>
                        )}
                    </div>
                )
            })}
        </div>
    )
}

export default BQHistoryList
